import { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface CopyButtonProps {
  text: string;
  size?: 'small' | 'default';
  ariaLabel?: string;
}

export const CopyButton = ({
  text,
  size = 'default',
  ariaLabel = 'Copy to clipboard',
}: CopyButtonProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e?: React.MouseEvent) => {
    e?.stopPropagation();

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const iconSize = size === 'small' ? 12 : 16;
  const buttonSize = size === 'small' ? 'h-4 w-4' : 'h-8 w-8';

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip open={copied}>
        <TooltipTrigger asChild>
          <button
            type='button'
            onClick={handleCopy}
            className={cn(
              'inline-flex items-center justify-center rounded-md',
              'text-muted-foreground hover:text-foreground',
              'transition-colors cursor-pointer',
              buttonSize,
            )}
            aria-label={ariaLabel}
          >
            {copied ? (
              <Check size={iconSize} className='text-green-600' />
            ) : (
              <Copy size={iconSize} />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Copied!</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
